import { Alert, Button, Card, DescriptionList } from '@ashniva/ui';

import { formatDateTime } from '../../../shared/lib/format';
import { shortCommit } from '../freshness';
import { useRepositoryIntelligenceAccess } from '../ri-access';
import { isServed, type RepositoryState } from '../ri-state';
import type { RepositoryStateResult } from '../use-repository-state';
import { useWorkspace } from '../workspace-context';
import { ProblemNotice } from './ProblemNotice';
import { RegistrationActions } from './RegistrationActions';
import { RepositoryStateView } from './RepositoryStateView';
import { SyncCard } from './SyncCard';
import { CardsSkeleton } from '../../../shared/components/LoadingSkeletons';

/** Whether the repository is known to Repository Intelligence at all, so that it has a sync history. */
function isRegistered(state: RepositoryState): boolean {
  return isServed(state) || state.kind === 'suspended' || state.kind === 'failed';
}

/**
 * The repository's state with Repository Intelligence (P14-02, P14-03, P14-08): where it is, how fresh, what to do
 * next — request indexing for people who manage it, "ask an administrator" for everyone else — and its updates.
 */
export function OverviewPanel({ repository }: { repository: RepositoryStateResult }) {
  const { linkId } = useWorkspace();
  const access = useRepositoryIntelligenceAccess();
  const { state, status, error, isLoading } = repository;

  if (isLoading) {
    return <CardsSkeleton label="the repository’s state" cards={2} />;
  }
  if (error || !state) {
    return <ProblemNotice error={error} onRetry={repository.checkAgain} />;
  }
  return (
    <div className="ri-stack">
      <Card title="Repository Intelligence">
        <RepositoryStateView
          state={state}
          manage={access.manage}
          actions={
            access.manage ? (
              <RegistrationActions
                linkId={linkId}
                state={state}
                onChanged={repository.checkAgain}
              />
            ) : undefined
          }
        />
        {repository.stillWorking ? (
          <Alert
            tone="info"
            title="Still in progress"
            action={<Button onClick={repository.checkAgain}>Check again</Button>}
          >
            Desk stopped checking after two minutes so as not to keep asking. Approval and copying
            can take a while.
          </Alert>
        ) : null}
        {status ? (
          <DescriptionList
            items={[
              {
                key: 'branch',
                term: 'Tracked branch',
                description: status.sync?.trackedBranch ?? '—',
              },
              {
                key: 'commit',
                term: 'Indexed commit',
                description: (
                  <span className="ri-mono">
                    {shortCommit(
                      status.sync?.indexedCommit ?? status.index.latestIngestion?.commit,
                    ) ?? '—'}
                  </span>
                ),
              },
              {
                key: 'behind',
                term: 'Up to date',
                description: upToDate(status.sync?.behind ?? null),
              },
              { key: 'files', term: 'Files indexed', description: String(status.index.files) },
              {
                key: 'checked',
                term: 'Last checked against the code host',
                description: formatDateTime(status.repository.verifiedAt),
              },
              {
                key: 'source',
                term: 'Answers from',
                description:
                  status.source === 'mock'
                    ? 'Mock data for development: nothing was read from a repository'
                    : 'Repository Intelligence',
              },
            ]}
          />
        ) : null}
      </Card>
      {isRegistered(state) ? <SyncCard linkId={linkId} manage={access.manage} /> : null}
    </div>
  );
}

function upToDate(behind: boolean | null): string {
  if (behind === null) return 'Not reported';
  return behind ? 'No: the code host has newer commits' : 'Yes';
}
